import React, { useEffect, useRef } from 'react';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { AUTH_TOKEN } from '../config';
import { audioEngine } from '../services/audioEngine';
import useLiveAvatarSession, { fmtClock } from '../hooks/useLiveAvatarSession';

// LiveChatDock — the live Anam video call, staged INTO the room.
//
// The WebRTC stream lands in a hidden DOM <video> (Anam's SDK streams to a
// video element); the room's VideoPanel picks that element up from the store
// (liveVideoEl) and paints it onto the SAME card the recorded cameo uses —
// the avatar behind the booth window goes live, mic open, our brain talking.
//
// Plain DOM: must render OUTSIDE the Canvas (r3f can't host DOM nodes).
// Mic + billing both require a click, so everything is button-gated.

const VIDEO_EL_ID = 'world-live-video';

const chip = {
  position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
  display: 'flex', alignItems: 'center', gap: 10, zIndex: 30,
  background: 'rgba(10,8,12,0.78)', border: '1px solid #ffb25e', borderRadius: 999,
  padding: '10px 16px', color: '#ffd27d', font: '14px system-ui, sans-serif',
  backdropFilter: 'blur(6px)', maxWidth: '86vw'
};
const btn = {
  background: '#ffb25e', color: '#1c1206', border: 'none', borderRadius: 999,
  padding: '7px 14px', font: '600 13px system-ui, sans-serif', cursor: 'pointer'
};
const ghostBtn = { ...btn, background: 'transparent', color: '#ffd27d', border: '1px solid #ffb25e' };

function Dock({ roomId, liveChat }) {
  const setLiveVideoEl = useWorldStore((s) => s.setLiveVideoEl);
  const {
    phase, message, status, costPerMinute, minutes, secondsLeft, live, start, endCall, reset,
  } = useLiveAvatarSession({ avatarId: liveChat.avatarId, videoElementId: VIDEO_EL_ID });

  // While live: hand the stream element to the 3D panel and duck the room's
  // sound bed so the conversation owns the mix.
  useEffect(() => {
    if (!live) return undefined;
    setLiveVideoEl(document.getElementById(VIDEO_EL_ID));
    audioEngine.duck(true);
    return () => { setLiveVideoEl(null); audioEngine.duck(false); };
  }, [live, setLiveVideoEl]);

  // Leaving the room mid-call ends (and refunds) the session.
  const endRef = useRef(endCall);
  endRef.current = endCall;
  const liveRef = useRef(false);
  liveRef.current = live || phase === 'connecting';
  useEffect(() => () => { if (liveRef.current) endRef.current('ended'); }, []);

  const name = liveChat.name || 'the artist';
  const canOffer = !status || status.allowed !== false; // no status yet ≠ blocked; start() gives the real error

  return (
    <>
      {/* The stream target — offscreen but PLAYING (not display:none, which
          some browsers throttle). Audio comes straight from this element. */}
      <video
        id={VIDEO_EL_ID}
        playsInline
        autoPlay
        style={{ position: 'fixed', right: 0, bottom: 0, width: 2, height: 2, opacity: 0.01, pointerEvents: 'none' }}
      />

      {phase === 'confirm' && canOffer && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>
            {name} is in the booth
            {costPerMinute != null ? ` · ${costPerMinute} credits/min · ${minutes} min` : ''}
          </span>
          <button style={btn} onClick={start}>Go live</button>
        </div>
      )}
      {phase === 'confirm' && !canOffer && (
        <div style={chip}><span style={{ opacity: 0.8 }}>Live chat isn't available for {name} yet.</span></div>
      )}

      {phase === 'connecting' && (
        <div style={chip}><span>Calling {name}… {message}</span></div>
      )}

      {live && (
        <div style={{ ...chip, borderColor: '#ff5e78' }}>
          <span style={{ color: '#ff5e78', fontWeight: 700 }}>● LIVE</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(secondsLeft)}</span>
          <span style={{ opacity: 0.75 }}>mic is open — just talk</span>
          <button style={ghostBtn} onClick={() => endCall('ended')}>End</button>
        </div>
      )}

      {(phase === 'ended' || phase === 'error') && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>{message}</span>
          <button style={ghostBtn} onClick={reset}>{phase === 'error' ? 'Try again' : 'OK'}</button>
        </div>
      )}
    </>
  );
}

export default function LiveChatDock() {
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const liveChat = ROOMS[interior]?.liveChat;
  if (zoom !== ZOOM.INTERIOR || !liveChat) return null;

  if (!AUTH_TOKEN) {
    return (
      <div style={chip}>
        <span style={{ opacity: 0.85 }}>
          Live chat needs your HypeTunes login — reload with <code>?token=&lt;your JWT&gt;</code> (saved after the first visit) or set VITE_AUTH_TOKEN.
        </span>
      </div>
    );
  }
  // key: fresh session state per room
  return <Dock key={interior} roomId={interior} liveChat={liveChat} />;
}
